import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { api } from "../lib/api.js";
import { haptic, openInvoice } from "../lib/telegram.js";
import { sound } from "../lib/sound.js";
import { useStore } from "../lib/store.js";

type PlanId = "month" | "quarter" | "half" | "year";

interface Plan {
  id: PlanId;
  months: number;
  stars: number;
  recurring: boolean;
  discountPct: number;
}

const PLAN_TITLE: Record<PlanId, string> = {
  month: "Месяц",
  quarter: "3 месяца",
  half: "6 месяцев",
  year: "Год",
};

export function Premium() {
  const { setScreen, premium, setPremium } = useStore();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [perks, setPerks] = useState<string[]>([]);
  const [busy, setBusy] = useState<PlanId | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = () =>
    api.billing().then((res) => {
      setPlans(res.plans as Plan[]);
      setPerks(res.perks as string[]);
      setPremium(res.entitlements);
    });

  useEffect(() => {
    let cancelled = false;
    api
      .billing()
      .then((res) => {
        if (cancelled) return;
        setPlans(res.plans as Plan[]);
        setPerks(res.perks as string[]);
        setPremium(res.entitlements);
      })
      .catch(() => setNote("Не удалось загрузить тарифы"));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function buy(plan: Plan) {
    setBusy(plan.id);
    setNote(null);
    haptic.tap();
    try {
      const { link } = await api.invoice(plan.id);
      openInvoice(link, (status) => {
        setBusy(null);
        if (status === "paid") {
          haptic.win();
          sound.win();
          setNote("Оплачено! Подписка активируется в течение пары секунд.");
          // Платёж подтверждает Telegram через вебхук — своих прав клиент
          // себе не выдаёт. Просто перечитываем их с сервера.
          setTimeout(() => void load().catch(() => {}), 1500);
          setTimeout(() => void load().catch(() => {}), 5000);
        } else if (status === "failed") {
          setNote("Платёж не прошёл");
        } else if (status === "cancelled") {
          setNote(null);
        }
      });
    } catch (err) {
      setBusy(null);
      setNote(err instanceof Error ? err.message : "Не удалось создать счёт");
    }
  }

  const until = premium?.until ? new Date(premium.until).toLocaleDateString("ru-RU") : null;

  return (
    <motion.div
      className="stack"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="row">
        <button
          className="btn"
          style={{ width: "auto", padding: "8px 14px" }}
          onClick={() => {
            haptic.tap();
            setScreen("lobby");
          }}
        >
          ← Назад
        </button>
        <div style={{ fontWeight: 700, fontSize: 18 }}>MiniGames Плюс</div>
      </div>

      {premium?.premium ? (
        <div className="card stack" style={{ gap: 6, borderColor: "var(--gold)" }}>
          <div style={{ fontWeight: 700 }}>Подписка активна</div>
          <div className="muted small">
            До {until}
            {premium.recurring ? " · продлевается автоматически" : " · без автопродления"}
          </div>
        </div>
      ) : (
        <div className="card stack" style={{ gap: 8 }}>
          <div style={{ fontWeight: 700 }}>Что даёт подписка</div>
          <ul className="rules-list rules-dots" style={{ paddingLeft: 18 }}>
            {perks.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
          <div className="muted small">
            Партии не ограничены и без подписки — платить нужно только за глубину.
          </div>
        </div>
      )}

      <div className="stack" style={{ gap: 8 }}>
        {plans.map((plan) => (
          <motion.button
            key={plan.id}
            className="plan-row"
            onClick={() => void buy(plan)}
            disabled={busy !== null}
            whileTap={{ scale: 0.98 }}
          >
            <span style={{ minWidth: 0, flex: 1, textAlign: "left" }}>
              <span style={{ display: "block", fontWeight: 700 }}>{PLAN_TITLE[plan.id]}</span>
              <span className="muted small">
                {plan.recurring ? "продлевается автоматически" : "разовая покупка"}
              </span>
            </span>
            {plan.discountPct > 0 && <span className="plan-badge">−{plan.discountPct}%</span>}
            <span className="plan-price">
              {busy === plan.id ? "…" : plan.stars} <span className="star">⭐</span>
            </span>
          </motion.button>
        ))}
      </div>

      {note && (
        <div className="muted small" style={{ textAlign: "center" }}>
          {note}
        </div>
      )}

      <div className="muted small" style={{ textAlign: "center", lineHeight: 1.5 }}>
        Оплата звёздами Telegram. Месячную подписку можно отменить в любой момент
        в настройках Telegram → Мои звёзды.
      </div>
    </motion.div>
  );
}
